import { ChangeEvent, ReactElement } from 'react';
import { FieldValues, useController } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ERRORS } from '@/configs/codes';
import { FILE_VALIDATORS } from '@/configs/validator';
import Button from '@/components/controls/Button/Button.component';
import styles from '@/components/controls/InputFile/InputFile.module.scss';
import { InputFileProps } from '@/components/controls/InputFile/InputFile.types';
import FieldError from '@/components/elems/FieldError/FieldError.component';

export const InputFile = <T extends FieldValues>({
    isDisabled,
    name,
    changeHandler,
    acceptType,
    children,
    control,
    className = '',
    isMulti,
    isCustom,
    isInlineError,
}: InputFileProps<T>): ReactElement => {
    const {
        field: { onChange },
        fieldState: { error },
    } = useController({ name, control });

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const file = event?.target?.files?.[0];

        if (!file) {
            onChange(file);
            return;
        }

        try {
            const reader = new FileReader();

            reader.addEventListener('load', () => {
                changeHandler?.(
                    typeof reader.result === 'string' ? reader.result : null,
                );
            });

            reader.readAsDataURL(file);
        } catch {
            toast.error(ERRORS.er270);
        }

        onChange(event.target.files);
    };

    return (
        <>
            <Button
                htmlFor={isDisabled ? undefined : name}
                className={className}
                dataTest={`${name}_label`}
                isCustom={isCustom}
                disabled={isDisabled}
            >
                <input
                    id={name}
                    name={name}
                    type="file"
                    accept={FILE_VALIDATORS[acceptType].accept.toString()}
                    className={styles.inputFile}
                    onChange={handleChange}
                    multiple={isMulti}
                    disabled={isDisabled}
                />
                {children}
            </Button>
            {isInlineError && <FieldError message={error?.message} />}
        </>
    );
};
